import { motion } from "framer-motion";
import { useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import worksheetsData from "../data/worksheets.json";

const withCacheBust = (src) => {
  if (!src) return src;
  return src.includes("?") ? `${src}&v=3` : `${src}?v=3`;
};

export default function Home() {

  const categories = [
    { name: "Math Worksheets", icon: "➕", subject: "Maths", slug: "maths" },
    { name: "English Worksheets", icon: "🔤", subject: "English", slug: "english" },
    { name: "Tracing", icon: "✏️", subject: "Tracing", slug: "tracing" },
    { name: "Coloring", icon: "🎨", subject: "Coloring", slug: "coloring" },
    { name: "Matching", icon: "🧩", subject: "Matching", slug: "matching" },
    { name: "Puzzle", icon: "🧠", subject: "Puzzle", slug: "puzzle" },
  ];

  const emojiBySubject = {
    Math: "➕",
    Maths: "➕",
    English: "🔤",
    Tracing: "✏️",
    Coloring: "🎨",
    Matching: "🧩",
    Puzzle: "🧠",
    Science: "🔬"
  };

  const trendingIds = [1, 2, 7, 11, 12, 15];
  const worksheets = worksheetsData
    .filter((w) => trendingIds.includes(w.id))
    .map((w) => {
      const titleLower = String(w.title || "").toLowerCase();
      const emoji =
        titleLower.includes("subtraction") ? "-" :
        titleLower.includes("addition") ? "+" :
        (emojiBySubject[w.subject] || "📄");
      return {
        id: w.id,
        title: w.title,
        emoji,
        file: w.file,
        preview: w.preview
      };
    });

  const [preview, setPreview] = useState(null);

  return (
    <div
      className="min-h-screen home-page"
      style={{ fontFamily: '"Baloo 2", "Comic Sans MS", "Trebuchet MS", sans-serif' }}
    >

      <Helmet>
        <title>PracticeNest – Free Printable Worksheets for LKG to Class 7</title>
        <meta
          name="description"
          content="PracticeNest provides free printable worksheets and practice sheets for LKG to Class 7 in Maths, English, Science and more."
        />
        <link rel="canonical" href="https://practicenest.in/" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link href="https://fonts.googleapis.com/css2?family=Baloo+2:wght@500;700;800&display=swap" rel="stylesheet" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "PracticeNest",
            url: "https://practicenest.in/",
            description: "Free printable worksheets and practice sheets for LKG to Class 7.",
            inLanguage: "en"
          })}
        </script>
      </Helmet>

      {/* NAVBAR */}

      <nav className="sticky top-0 z-20 flex justify-between items-center px-6 md:px-10 py-4 bg-white shadow home-nav">
        <h1 className="text-2xl font-extrabold">🪺 PracticeNest</h1>

        <div className="flex items-center gap-4 md:gap-6 text-sm md:text-base">

          <Link to="/">Home</Link>

          <Link to="/grades" state={{ fromApp: true }}>
            <button className="bg-black text-white px-4 py-2 rounded-full shadow hover:scale-105 transition pn-button pn-button--dark">
              Worksheets
            </button>
          </Link>

          <Link to="/grades" state={{ fromApp: true }}>Grade</Link>
          <Link to="/subjects" state={{ fromApp: true }}>Subjects</Link>
          <Link to="/practice">Online Practice</Link>
          <a href="#contact">Contact</a>

        </div>
      </nav>


      {/* HERO */}

      <section className="px-6 md:px-12 py-16 md:py-20 hero-section">

        <div className="hero-decor">
          <span className="hero-blob hero-blob-1" />
          <span className="hero-blob hero-blob-2" />
          <span className="hero-blob hero-blob-3" />
        </div>

        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-10 items-center relative z-10">
          <div>
            <div className="text-sm md:text-base font-bold tracking-widest uppercase text-blue-600 mb-3">
              Learn. Play. Grow. Every Day.
            </div>
            <motion.h1
              initial={{ opacity:0 , y:-30 }}
              animate={{ opacity:1 , y:0 }}
              transition={{ duration:1 }}
              className="text-5xl md:text-6xl font-extrabold mb-6 leading-tight"
            >
              Happy Learning Worksheets 🧸
            </motion.h1>

            <p className="text-lg md:text-xl mb-6">
              Bright, bite‑size practice sheets for Maths, English and more.
            </p>

            <div className="flex flex-wrap gap-4 mb-6">
              <Link to="/grades" state={{ fromApp: true }}>
                <button className="bg-blue-600 text-white px-8 py-4 rounded-full text-lg shadow hover:scale-105 transition pn-button pn-button--blue">
                  Browse Worksheets
                </button>
              </Link>
              <Link to="/grades" state={{ fromApp: true }}>
                <button className="bg-white text-black px-7 py-4 rounded-full text-lg border border-black/10 shadow hover:scale-105 transition pn-button pn-button--light">
                  Choose Grade
                </button>
              </Link>
            </div>

            <div className="flex flex-wrap gap-3 text-sm">
              <span className="bg-yellow-300/80 px-3 py-1 rounded-full font-semibold pill-badge">Free Printable</span>
              <span className="bg-green-200 px-3 py-1 rounded-full font-semibold pill-badge">Kid‑Friendly</span>
              <span className="bg-pink-200 px-3 py-1 rounded-full font-semibold pill-badge">No Sign‑Up</span>
            </div>
          </div>

          <div className="relative">
          <div className="bg-white p-6 rounded-3xl shadow border border-black/5 hero-card">
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs uppercase tracking-widest text-blue-600 font-bold">Today’s Spark</span>
                <span className="text-2xl">✨</span>
              </div>
              <div className="text-2xl font-bold mb-2">Maths: Add & Play</div>
              <p className="text-sm text-gray-600 mb-4">
                Quick 5‑minute practice with fun numbers and big smiles.
              </p>
              <div className="grid grid-cols-3 gap-3 text-center text-3xl">
                <div>➕</div>
                <div>🧩</div>
                <div>🎯</div>
              </div>
              <div className="mt-5 flex gap-2 text-sm">
                <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full">LKG‑II</span>
                <span className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full">Printable</span>
              </div>
            </div>
          </div>
        </div>

      </section>


      {/* CATEGORIES */}

      <section className="px-6 md:px-10 py-16">

        <h2 className="text-3xl font-bold text-center mb-12">
          Popular Categories
        </h2>

        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-8">

          {categories.map((c,index)=>(
            <Link
              key={index}
              to={`/worksheets/subject/${c.slug}`}
              className="bg-white text-center border border-black/5 w-full category-card pn-card"
              style={{ textDecoration: "none", color: "inherit" }}
            >
              <div className="text-5xl mb-4">{c.icon}</div>
              <p className="font-semibold">{c.name}</p>
            </Link>
          ))}

        </div>

      </section>


      {/* ONLINE PRACTICE */}

      <section className="px-6 md:px-10 py-16">
        <div className="max-w-6xl mx-auto bg-white/90 rounded-3xl shadow-xl border border-black/5 p-10 text-center">
          <h2 className="text-3xl font-bold mb-3">Online Practice</h2>
          <p className="text-lg mb-6">Solve worksheets online and get instant feedback.</p>
          <Link to="/practice">
            <button className="bg-black text-white px-8 py-4 rounded-full text-lg shadow hover:scale-105 transition pn-button pn-button--dark">
              Start Online Practice
            </button>
          </Link>
        </div>
      </section>


      {/* WORKSHEETS */}

      <section className="px-6 md:px-10 py-16 bg-white">

        <h2 className="text-3xl font-bold text-center mb-12">
          Trending Worksheets
        </h2>

        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-8">

          {worksheets.map((w)=>(
            <div
              key={w.id}
              className="bg-gray-50 p-10 rounded-2xl text-center border border-black/5 pn-card"
            >

              <div className="text-6xl mb-6">{w.emoji}</div>

              <p className="font-semibold mb-4">{w.title}</p>

              <div className="flex items-center justify-center gap-3">
                <button
                  className="bg-blue-600 text-white px-5 py-2 rounded-full pn-button pn-button--blue"
                  onClick={() => setPreview(w)}
                >
                  Preview
                </button>
                <a href={w.file} target="_blank" rel="noreferrer">
                  <button className="bg-white text-black px-5 py-2 rounded-full border border-black/10 pn-button pn-button--light">
                    Download
                  </button>
                </a>
              </div>

            </div>
          ))}

        </div>

      </section>

      {preview && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
            zIndex: 50
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: "720px",
              background: "#fff",
              borderRadius: "16px",
              padding: "20px",
              boxShadow: "0 16px 30px rgba(0,0,0,0.2)"
            }}
          >
            <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
              <h3 style={{margin: 0}}>{preview.title}</h3>
              <button
                onClick={() => setPreview(null)}
                style={{
                  border: "1px solid #bbb",
                  background: "#fff",
                  borderRadius: "8px",
                  padding: "6px 10px",
                  cursor: "pointer"
                }}
              >
                Close
              </button>
            </div>
            <div style={{marginTop: "12px"}}>
              <img
                src={preview.preview}
                alt="worksheet preview"
                style={{width: "100%", height: "auto", display: "block", borderRadius: "10px"}}
              />
            </div>
            <div style={{marginTop: "14px", display: "flex", gap: "10px"}}>
              <a
                href={withCacheBust(preview.file)}
                download
                style={{
                  padding:"10px 14px",
                  fontSize:"14px",
                  fontWeight: "600",
                  background: "#2D7FF9",
                  color: "#fff",
                  border: "none",
                  borderRadius: "10px",
                  cursor: "pointer",
                  textDecoration: "none"
                }}
              >
                Download
              </a>
            </div>
          </div>
        </div>
      )}


      {/* WHY SECTION */}

      <section className="px-6 md:px-10 py-16">

        <h2 className="text-3xl font-bold text-center mb-12">
          Why Parents Love PracticeNest ❤️
        </h2>

        <div className="max-w-6xl mx-auto grid md:grid-cols-4 gap-6 text-center">

          <div className="bg-white p-6 rounded-2xl shadow border border-black/5">
            📄 Free Printable Worksheets
          </div>

          <div className="bg-white p-6 rounded-2xl shadow border border-black/5">
            👶 Designed for Kids
          </div>

          <div className="bg-white p-6 rounded-2xl shadow border border-black/5">
            📚 Improve Learning Skills
          </div>

          <div className="bg-white p-6 rounded-2xl shadow border border-black/5">
            🎯 Fun Activities
          </div>

        </div>

      </section>


      {/* CTA */}

      <section className="text-center py-20 bg-gradient-to-r from-yellow-200 via-orange-200 to-pink-200">

        <h2 className="text-4xl font-bold mb-6">
          Start Learning Today 🚀
        </h2>

        <Link to="/grades" state={{ fromApp: true }}>
          <button className="bg-black text-white px-10 py-4 rounded-full text-lg shadow hover:scale-105 transition pn-button pn-button--dark">
            Explore Worksheets
          </button>
        </Link>

      </section>

      {/* CONTACT */}

      <section id="contact" className="px-6 md:px-10 py-16">
        <div className="max-w-4xl mx-auto bg-white/95 rounded-3xl shadow-xl border border-black/5 p-8 md:p-10 text-center">
          <h2 className="text-3xl font-bold mb-4">Contact Us</h2>
          <p className="text-lg mb-6">
            For more details, email us at
            <span className="font-bold"> kidslearningnest@gmail.com</span>
          </p>
          <div className="flex justify-center gap-6 text-2xl">
            <a
              href="https://www.facebook.com"
              target="_blank"
              rel="noreferrer"
              className="bg-blue-100 text-blue-700 px-4 py-2 rounded-full"
            >
              📘 Facebook
            </a>
            <a
              href="https://www.instagram.com"
              target="_blank"
              rel="noreferrer"
              className="bg-pink-100 text-pink-700 px-4 py-2 rounded-full"
            >
              📸 Instagram
            </a>
          </div>
        </div>
      </section>


      {/* FOOTER */}

      <footer className="bg-gray-900 text-white text-center p-8">

        <p>© 2026 PracticeNest</p>

      </footer>

    </div>
  );
}
